import { useRef } from "react";
import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

export interface EnrollableEdition {
  id: number;
  editionCode?: string;
  editionName?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  totalPlaces?: number;
  occupiedPlaces?: number;
  remainingPlaces?: number;
}

interface FetchEnrollableArgs {
  corporateId: string | number;
  objectId: string | number;
  studentEid: string | number;
}

interface RescheduleArgs {
  corporateId: string | number;
  objectId: string | number;
  studentEid: string | number;
  newEditionId: number;
}

export type RescheduleOutcome =
  | { status: "COMPLETE" }
  | { status: "ERROR"; errorMessage?: string }
  | { status: "TIMEOUT" };

const POLL_MAX_DURATION_MS = 120_000;
const POLL_INITIAL_INTERVAL_MS = 5_000;
const POLL_INTERVAL_STEP_MS = 5_000;
const POLL_MAX_INTERVAL_MS = 20_000;

export const useRescheduleEnrollment = () => {
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { trigger: triggerGetEnrollable, isMutating: isFetchingEditions } =
    useSWRMutation(
      ServicesURL.getEnrollableEditions,
      (url: string, { arg }: { arg: FetchEnrollableArgs }) =>
        fetcher<{ editions?: EnrollableEdition[] }>(
          `${url}?corporateId=${arg.corporateId}&objectId=${arg.objectId}&userId=${arg.studentEid}`,
          MethodType.GET,
        ),
    );

  const { trigger: triggerPatchEnrollment, isMutating: isPatching } =
    useSWRMutation(
      ServicesURL.patchEnrollment,
      (url: string, { arg }: { arg: any[] }) =>
        fetcher<any>(url, MethodType.PATCH, arg),
    );

  const { trigger: triggerGetStatus } = useSWRMutation(
    ServicesURL.getEnrollmentStatus,
    (
      url: string,
      {
        arg,
      }: { arg: { corporateId: string; editionId: string; userId: string } },
    ) =>
      fetcher<{ status?: string; errorMessage?: string }>(
        `${url}?corporateId=${arg.corporateId}&editionId=${arg.editionId}&userId=${arg.userId}`,
        MethodType.GET,
      ),
    { onError: () => null },
  );

  const fetchEnrollableEditions = async (
    args: FetchEnrollableArgs,
  ): Promise<EnrollableEdition[]> => {
    const res = await triggerGetEnrollable(args);
    return res?.editions ?? [];
  };

  const pollUntilTerminal = async (
    args: RescheduleArgs,
  ): Promise<RescheduleOutcome> => {
    const startTime = Date.now();
    let interval = POLL_INITIAL_INTERVAL_MS;

    return new Promise<RescheduleOutcome>((resolve) => {
      const tick = async () => {
        const elapsed = Date.now() - startTime;
        if (elapsed >= POLL_MAX_DURATION_MS) {
          resolve({ status: "TIMEOUT" });
          return;
        }
        try {
          const res = await triggerGetStatus({
            corporateId: String(args.corporateId),
            editionId: String(args.newEditionId),
            userId: String(args.studentEid),
          });
          const status = res?.status;
          if (status === "COMPLETE") {
            resolve({ status: "COMPLETE" });
            return;
          }
          if (status === "ERROR") {
            resolve({ status: "ERROR", errorMessage: res?.errorMessage });
            return;
          }
        } catch {
          // ignore transient errors and keep polling
        }
        if (interval < POLL_MAX_INTERVAL_MS) interval += POLL_INTERVAL_STEP_MS;
        pollTimeoutRef.current = setTimeout(tick, interval);
      };
      pollTimeoutRef.current = setTimeout(tick, interval);
    });
  };

  const cancelPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const submitReschedule = async (args: RescheduleArgs): Promise<void> => {
    await triggerPatchEnrollment([
      {
        corporateId: Number(args.corporateId),
        editionId: args.newEditionId,
        objectId: Number(args.objectId),
        isToj: false,
        userId: [args.studentEid],
        mandatory: null,
        isAdmin: true,
      },
    ]);
  };

  const awaitOutcome = (args: RescheduleArgs): Promise<RescheduleOutcome> =>
    pollUntilTerminal(args);

  return {
    fetchEnrollableEditions,
    submitReschedule,
    awaitOutcome,
    cancelPolling,
    isFetchingEditions,
    isSubmitting: isPatching,
  };
};
